import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import JsonLd from "@/components/feature/JsonLd";
import {
  buildArticleSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  toLanguageTag,
} from "@/seo/schema";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/mocks/categories";

interface FaqItem {
  q: string;
  a: string;
}

interface TableRow {
  area: string;
  what: string;
  notes: string;
}

export default function Article() {
  const { t, i18n } = useTranslation();

  const toc = t("article.toc", { returnObjects: true }) as unknown as string[];
  const steps = t("article.s4Steps", { returnObjects: true }) as unknown as string[];
  const rows = t("article.s3Rows", { returnObjects: true }) as unknown as TableRow[];
  const faq = t("article.faq", { returnObjects: true }) as unknown as FaqItem[];

  const dateStr = new Date("2026-08-20").toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const tocIds = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "faq"];

  return (
    <>
      <JsonLd
        data={[
          buildOrganizationSchema(),
          buildArticleSchema({
            headline: t("article.h1"),
            description: t("meta.article.description"),
            path: "/usfans-spreadsheet-guide",
            dateModified: "2026-08-20",
            language: toLanguageTag(i18n.language),
            category: t("article.categoryLabel"),
          }),
          buildBreadcrumbSchema([
            { name: t("nav.home"), path: "/" },
            { name: t("article.categoryLabel"), path: "/usfans-spreadsheet-guide" },
          ]),
          buildFaqSchema(faq),
        ]}
      />
      <Header />
      <main className="bg-background-50">
        <div className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">
              {t("nav.home")}
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-800">{t("article.categoryLabel")}</span>
          </nav>

          {/* Header */}
          <header className="mt-6">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </span>
              {t("article.categoryLabel")}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              {t("article.h1")}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-user-line"></i>
                </span>
                {t("article.author")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-calendar-line"></i>
                </span>
                {t("article.published")}: {dateStr}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-time-line"></i>
                </span>
                7 {t("article.readTime")}
              </span>
            </div>
          </header>

          {/* Intro */}
          <div className="mt-8 space-y-4">
            <p className="text-base leading-relaxed text-foreground-700">{t("article.intro")}</p>
            <p className="text-base leading-relaxed text-foreground-700">{t("article.intro2")}</p>
          </div>

          {/* TOC */}
          <nav aria-label={t("article.tocTitle")} className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-foreground-900">
              {t("article.tocTitle")}
            </h2>
            <ol className="mt-3 space-y-2">
              {toc.map((item, i) => (
                <li key={i}>
                  <a
                    href={`#${tocIds[i]}`}
                    className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                  >
                    <span className="font-semibold text-primary-600">{i + 1}.</span>
                    {item}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Sections */}
          <article className="mt-10 space-y-12">
            <section id="s1" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s1Title")}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s1Text")}</p>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s1Text2")}</p>
            </section>

            <section id="s2" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s2Title")}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s2Text")}</p>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s2Text2")}</p>
            </section>

            <section id="s3" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s3Title")}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s3Text")}</p>
              <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-background-100 text-foreground-700">
                      <th className="px-4 py-3 font-semibold">{t("article.s3Table.area")}</th>
                      <th className="px-4 py-3 font-semibold">{t("article.s3Table.what")}</th>
                      <th className="px-4 py-3 font-semibold">{t("article.s3Table.notes")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t border-background-200">
                        <td className="px-4 py-3 font-medium text-foreground-900">{r.area}</td>
                        <td className="px-4 py-3 text-foreground-600">{r.what}</td>
                        <td className="px-4 py-3 text-foreground-600">{r.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="s4" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s4Title")}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s4Text")}</p>
              <ol className="mt-5 space-y-3">
                {steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-background-50">
                      {i + 1}
                    </span>
                    <span className="pt-0.5 text-sm leading-relaxed text-foreground-700">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section id="s5" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s5Title")}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s5Text")}</p>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s5Text2")}</p>
            </section>

            <section id="s6" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s6Title")}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s6Text")}</p>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s6Text2")}</p>
            </section>

            <section id="s7" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s7Title")}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s7Text")}</p>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s7Text2")}</p>
            </section>

            {/* FAQ */}
            <section id="faq" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.faqTitle")}</h2>
              <div className="mt-5 space-y-3">
                {faq.map((item, i) => (
                  <div key={i} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 md:text-base">
                      {item.q}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related */}
            <section className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">
                {t("article.relatedTitle")}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {t("article.relatedBody")}
              </p>
              <Link
                to="/"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("article.backHome")}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
              </Link>
            </section>

            {/* CTA */}
            <section className="rounded-lg bg-primary-500 px-6 py-10 text-center">
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                {t("article.ctaTitle")}
              </h2>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-background-100">
                {t("article.ctaBody")}
              </p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                {t("article.ctaButton")}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
            </section>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}